const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

class ApiService {
  constructor() {
    this.baseUrl = API_URL + '/api';
    this.token = localStorage.getItem('r2_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('r2_token', token);
    } else {
      localStorage.removeItem('r2_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('r2_token');
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  // ── Auth ──
  async register(data) {
    const result = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    this.setToken(result.accessToken);
    return result;
  }

  async login(email, password) {
    const result = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(result.accessToken);
    return result;
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('r2_user');
  }

  // ── Users ──
  getMe() {
    return this.request('/users/me');
  }

  // ── Listings ──
  getListings(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/listings?${query}`);
  }

  getListing(id) {
    return this.request(`/listings/${id}`);
  }

  createListing(data) {
    return this.request('/listings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateListing(id, data) {
    return this.request(`/listings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  deleteListing(id) {
    return this.request(`/listings/${id}`, { method: 'DELETE' });
  }

  getMyListings() {
    return this.request('/listings/seller/mine');
  }

  // ── Offers ──
  submitOffer(listingId, amount) {
    return this.request('/offers', {
      method: 'POST',
      body: JSON.stringify({ listingId, amount }),
    });
  }

  getMyOffers() {
    return this.request('/offers/mine');
  }

  getListingOffers(listingId) {
    return this.request(`/offers/listing/${listingId}`);
  }

  // ── Auctions ──
  confirmPayment(listingId) {
    return this.request(`/auctions/${listingId}/confirm-payment`, { method: 'POST' });
  }

  addShipping(listingId, shippingInfo) {
    return this.request(`/auctions/${listingId}/shipping`, {
      method: 'POST',
      body: JSON.stringify({ shippingInfo }),
    });
  }

  // ── Files ──
  async uploadListingFiles(listingId, files) {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));

    const token = this.getToken();
    const response = await fetch(`${this.baseUrl}/files/listing/${listingId}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Upload failed');
    }
    return response.json();
  }

  async uploadR2Certificate(file) {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.getToken();
    const response = await fetch(`${this.baseUrl}/files/r2-certificate`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Upload failed');
    }
    return response.json();
  }
}

const api = new ApiService();
export default api;
